import React, { useEffect, useMemo, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useContextStateManagement } from "../../context/ContextApi";
import CallLegsDialog from "../../child-components/CallDialogs/CallLegsDialog";
import { usePracticeTimezone } from "../../hooks/usePracticeTimezone";
import { formatUsDateInPracticeTimezone } from "../../practiceTimezone";

const formatDateTime = (value, timeZone) =>
  value ? formatUsDateInPracticeTimezone(value, timeZone) : "-";

const formatDuration = (value) => {
  const totalSeconds = Number(value || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const formatLabel = (value) => {
  const formatted = String(value || "").toLowerCase();
  if (formatted === "no_answer") return "No Answer";
  return formatted
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const mapLegacyRow = (row, workspace) => ({
  ...row,
  summaryWorkspace: workspace,
  DisplayStartedAt:
    row?.DisplayStartedAt || row?.StartedAt || row?.CreateDateTime || row?.DateCreated || "",
  DisplayEndedAt:
    row?.DisplayEndedAt || row?.EndedAt || row?.EndTime || row?.DateUpdated || "",
  totalSeconds:
    Number(row?.totalSeconds) ||
    Number(row?.DurationSec) ||
    Number(row?.TotalDurationSec) ||
    Number(row?.Duration) ||
    0,
  billableSeconds:
    Number(row?.billableSeconds) ||
    Number(row?.TotalAllLegsDurationSec) ||
    Number(row?.BillableSeconds) ||
    Number(row?.DurationSec) ||
    Number(row?.Duration) ||
    0,
  legCount:
    Number(row?.legCount) ||
    Number(row?.LegCount) ||
    (Array.isArray(row?.legs) ? row.legs.length : 1),
  hasTransfer:
    row?.hasTransfer === true ||
    Number(row?.LegCount) > 1 ||
    (Array.isArray(row?.legs) ? row.legs.length > 1 : false),
  id: `${workspace}-${row?.RootCallSid || "root"}-${row?.LegSid || row?.Sid || row?.Id || row?.DisplayStartedAt || row?.CreateDateTime || "row"}`,
});

export default function SummaryPromptHub({ onBack }) {
  const { PracticeId, inboundContact, outBoundContact, token, dateRangeRequest } =
    useContextStateManagement();
  const practiceTimezone = usePracticeTimezone(PracticeId, token);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [legsDialogOpen, setLegsDialogOpen] = useState(false);
  const [legsDialogGroup, setLegsDialogGroup] = useState(null);

  const loadRows = async () => {
    if (!PracticeId || !token) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [inboundResponse, outboundResponse] = await Promise.all([
        dateRangeRequest(token, "/api/getInboundCalls", { inboundContact }),
        dateRangeRequest(token, "/api/getCalls", { fromContact: outBoundContact }),
      ]);

      const inboundRows =
        inboundResponse === "no record" || inboundResponse === "Token is expired, login again"
          ? []
          : (Array.isArray(inboundResponse) ? inboundResponse : []).map((row) =>
              mapLegacyRow(row, "inbound"),
            );

      const outboundRows =
        outboundResponse === "no record" || outboundResponse === "Token is expired, login again"
          ? []
          : (Array.isArray(outboundResponse) ? outboundResponse : []).map((row) =>
              mapLegacyRow(row, "outbound"),
            );

      setRows(
        [...inboundRows, ...outboundRows].sort(
          (left, right) =>
            new Date(right?.DisplayStartedAt || 0).getTime() -
            new Date(left?.DisplayStartedAt || 0).getTime(),
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, inboundContact, outBoundContact, token]);

  const columns = useMemo(
    () => [
      {
        field: "DisplayStartedAt",
        headerName: "Started",
        width: 180,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (params) => formatDateTime(params.value, practiceTimezone),
      },
      {
        field: "DisplayEndedAt",
        headerName: "Ended",
        width: 180,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (params) => formatDateTime(params.value, practiceTimezone),
      },
      {
        field: "totalSeconds",
        headerName: "Total",
        width: 110,
        disableColumnMenu: true,
        renderCell: (params) => formatDuration(params.value),
      },
      {
        field: "billableSeconds",
        headerName: "Billable",
        width: 110,
        disableColumnMenu: true,
        renderCell: (params) => formatDuration(params.value),
      },
      {
        field: "legCount",
        headerName: "Legs",
        width: 100,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => {
          const count = params.value || 1;
          return (
            <button
              type="button"
              className="simple-btn"
              style={{ padding: "2px 8px", fontSize: 12 }}
              onClick={(event) => {
                event.stopPropagation();
                setLegsDialogGroup({
                  rootCallSid: params.row?.RootCallSid || params.row?.Sid,
                  legs: params.row?.legs,
                  totalSeconds: params.row?.totalSeconds,
                  billableSeconds: params.row?.billableSeconds,
                  legCount: params.row?.legCount,
                  hasTransfer: params.row?.hasTransfer,
                });
                setLegsDialogOpen(true);
              }}
            >
              View ({count})
            </button>
          );
        },
      },
      {
        field: "PracticeDirection",
        headerName: "Practice Dir",
        width: 140,
        disableColumnMenu: true,
        renderCell: (params) => formatLabel(params.value),
      },
      {
        field: "Direction",
        headerName: "Twilio Dir",
        width: 160,
        disableColumnMenu: true,
        renderCell: (params) => formatLabel(params.value),
      },
      {
        field: "LegLevel",
        headerName: "Root/Child",
        width: 110,
        disableColumnMenu: true,
        renderCell: (params) => String(params.value || "").toUpperCase(),
      },
      {
        field: "From",
        headerName: "From",
        width: 220,
        filterable: true,
      },
      {
        field: "To",
        headerName: "To",
        width: 260,
        filterable: true,
      },
    ],
    [practiceTimezone],
  );

  return (
    <>
      <div className="d-flex justify-content-end gap-2 mb-3">
        <button type="button" className="simple-btn" onClick={onBack}>
          Back to Summary
        </button>
      </div>
      <div className="grid-layout">
        <DataGrid
          className="call-log-grid"
          rows={rows}
          rowHeight={56}
          columns={columns}
          autoWidth
          pageSizeOptions={[10, 25, 50, 100]}
          loading={loading}
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          getRowId={(row) => row.id}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
        />
      </div>
      <CallLegsDialog
        open={legsDialogOpen}
        onClose={() => setLegsDialogOpen(false)}
        rootCallSid={legsDialogGroup?.rootCallSid}
        group={legsDialogGroup}
      />
    </>
  );
}
